import React, { useState, useEffect, useRef } from "react";
import { X, ArrowLeft } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { SockProduct } from "../models/sock-product";
import ProductDetail from "../views/products/subviews/product_detail";

interface SellexProductContainerProps {
  product: SockProduct;
  selectedCurrency: string;
}

const currencySymbols: { [key: string]: string } = {
  SAR: "SAR",
  AED: "AED",
  BHD: "BD",
  INR: "₹",
};

export const SellexProductContainer: React.FC<SellexProductContainerProps> = ({
  product,
  selectedCurrency,
}) => {
  const [showDetail, setShowDetail] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [modalAnimation, setModalAnimation] = useState("");
  const navigate = useNavigate();

  // Check if mobile viewport
  const isMobile = window.innerWidth < 768;

  // Refs to manage history/popstate logic
  const pushedStateRef = useRef(false); // whether we pushed a history state when modal opened
  const suppressPopRef = useRef(false); // used to ignore the next popstate when we call history.back()

  const handleProductClick = () => {
    if (isMobile) {
      // On mobile, show modal with slide-up animation
      setModalAnimation("animate-slide-up");
      setShowDetail(true);

      // push a history state so the back button will fire popstate
      try {
        window.history.pushState(
          { sellexxProductModal: product.id },
          "",
          `#product-${product.id}`
        );
        pushedStateRef.current = true;
      } catch {
        // ignore if pushState fails for some reason
        pushedStateRef.current = false;
      }
    } else {
      // On desktop, navigate to dedicated product page
      navigate(`/product/${product.id}`);
    }
  };

  // Lock body scroll when modal is open
  useEffect(() => {
    if (showDetail) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }
    return () => {
      document.body.style.overflow = "unset";
    };
  }, [showDetail]);

  // popstate listener: close modal when back button/hardware back pressed
  useEffect(() => {
    const onPopState = () => {
      // If we set suppress flag (because we triggered history.back programmatically), ignore this pop
      if (suppressPopRef.current) {
        suppressPopRef.current = false;
        return;
      }

      if (showDetail) {
        // play close animation then hide modal
        setModalAnimation("animate-slide-down");
        setTimeout(() => {
          setShowDetail(false);
          setModalAnimation("");
          // reset pushed state flag because the back already removed it
          pushedStateRef.current = false;
        }, 300);
      }
    };

    window.addEventListener("popstate", onPopState);
    return () => window.removeEventListener("popstate", onPopState);
  }, [showDetail]);

  const closeModal = () => {
    setModalAnimation("animate-slide-down");

    setTimeout(() => {
      setShowDetail(false);
      setModalAnimation("");

      // If we pushed a history entry earlier, go back to remove it.
      // We suppress the next popstate because our popstate handler would also close the modal otherwise.
      if (pushedStateRef.current) {
        suppressPopRef.current = true;
        try {
          window.history.back();
        } catch {
          // ignore
        }
        pushedStateRef.current = false;
      }
    }, 300);
  };

  const pricing =
    product.prices[selectedCurrency as keyof typeof product.prices] ||
    product.prices["SAR"];
  const discountPercentage = Math.round(
    ((pricing.mrp - pricing.price) / pricing.mrp) * 100
  );

  return (
    <>
      <div
        className="group cursor-pointer relative h-full overflow-hidden rounded-2xl bg-white transition-all duration-500 shadow-lg hover:shadow-2xl hover:-translate-y-1"
        onClick={handleProductClick}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        {discountPercentage > 0 && (
          <div className="absolute left-3 top-3 z-10 rounded-full bg-gradient-to-r from-red-500 to-pink-500 px-3 py-1.5 text-xs font-bold text-white shadow-lg backdrop-blur-sm transform transition-transform duration-300 group-hover:scale-110">
            {discountPercentage}% OFF
          </div>
        )}

        <div className="relative aspect-square overflow-hidden bg-gray-50">
          <img
            src={product.featuredImage.thumbnailUrl}
            alt={product.name}
            className={`h-full w-full object-cover transition-all duration-700 ease-out ${
              isHovered ? "scale-110 brightness-110" : "scale-100"
            }`}
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/20 via-transparent to-transparent opacity-0 transition-opacity duration-300 group-hover:opacity-100" />

          <div className="absolute inset-0 bg-gradient-to-br from-blue-500/10 to-purple-500/10 opacity-0 transition-opacity duration-500 group-hover:opacity-100" />
        </div>

        <div className="space-y-3 p-4 bg-gradient-to-t from-white to-gray-50/50">
          <h3 className="text-sm font-semibold text-gray-900 line-clamp-2 md:text-base leading-tight">
            {product.name}
          </h3>

          <div className="text-xs text-gray-500 uppercase tracking-wide">
            {product.category}
          </div>
        </div>
      </div>

      {/* Mobile Modal */}
      {showDetail && isMobile && (
        <>
          <div className="fixed inset-0 z-40 bg-black/50 backdrop-blur-sm animate-fade-in" />
          <div className={`fixed inset-0 z-50 ${modalAnimation}`}>
            <div className="absolute inset-0 bg-white">
              {/* Header */}
              <div className="sticky top-0 z-10 bg-white/95 backdrop-blur-md border-b border-gray-200 px-4 py-3">
                <div className="flex items-center justify-between">
                  <button
                    onClick={closeModal}
                    className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors p-2 -ml-2 rounded-lg hover:bg-gray-100"
                  >
                    <ArrowLeft className="w-5 h-5" />
                    <span className="text-sm font-medium">Back</span>
                  </button>
                  <button
                    onClick={closeModal}
                    className="p-2 rounded-full hover:bg-gray-100 transition-colors"
                  >
                    <X className="h-5 w-5 text-gray-600" />
                  </button>
                </div>
              </div>

              {/* Content */}
              <div className="overflow-y-auto h-full pb-20">
                <ProductDetail
                  product={product}
                  selectedCurrency={selectedCurrency}
                />
              </div>
            </div>
          </div>
        </>
      )}

      {/* Custom animations */}
      <style>{`
        @keyframes slideUp {
          from { transform: translateY(100%); }
          to   { transform: translateY(0); }
        }
        @keyframes slideDown {
          from { transform: translateY(0); }
          to   { transform: translateY(100%); }
        }
        @keyframes fadeIn {
          from { opacity: 0; }
          to   { opacity: 1; }
        }

        .animate-slide-up { animation: slideUp 0.4s cubic-bezier(0.16, 1, 0.3, 1); }
        .animate-slide-down { animation: slideDown 0.3s cubic-bezier(0.16, 1, 0.3, 1); }
        .animate-fade-in { animation: fadeIn 0.3s ease-out; }
      `}</style>
    </>
  );
};

export default SellexProductContainer;
